import {
  Color as ThreeColor,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera as ThreeOrthographicCamera,
  PlaneGeometry,
  Scene as ThreeScene,
  SRGBColorSpace,
  Texture as ThreeTexture,
  WebGLRenderer,
} from 'three';
import { type EntityId } from '../../entities/EntityId';
import { type Color } from '../../math/Color';
import { rotatedX, rotatedY } from '../../math/MathUtils';
import { type Camera } from '../Camera';
import { type CanvasSize } from '../CanvasSize';
import { type Renderer, type SpriteDrawCall } from '../Renderer';
import { compareSpriteDrawOrder } from '../SpriteDrawOrder';
import { type Texture, type TextureSource } from '../Texture';
import { type Viewport } from '../Viewport';
import { toWebGLRect, type WebGLRect } from './WebGLViewport';

const CAMERA_DISTANCE = 10;
const CAMERA_NEAR = 0.1;
const CAMERA_FAR = 100;
const SPRITE_DEPTH = 0;

/** Fills the letterbox bars. Deliberately not the scene's clear color, so the playfield reads apart. */
const LETTERBOX_COLOR = 0x000000;

/** A texture created by {@link ThreeRenderer}, carrying the backend object the adapter needs back. */
class ThreeTextureHandle implements Texture {
  readonly #texture: ThreeTexture;

  constructor(
    texture: ThreeTexture,
    readonly width: number,
    readonly height: number,
  ) {
    this.#texture = texture;
  }

  /** The backend texture. Only the adapter that made it reads this. */
  get texture(): ThreeTexture {
    return this.#texture;
  }

  /** Releases the GPU resource. */
  dispose(): void {
    this.#texture.dispose();
  }
}

interface RetainedSprite {
  readonly entityId: EntityId;
  readonly mesh: Mesh<PlaneGeometry, MeshBasicMaterial>;
  sortOrder: number;
  seen: boolean;
}

/** Construction options for {@link ThreeRenderer}. */
export interface ThreeRendererOptions {
  /** Canvas to draw into, normally the one a `CanvasSurface` owns. */
  readonly canvas: HTMLCanvasElement;
  /** Whether to request a multisampled drawing buffer. */
  readonly antialias?: boolean;
}

/**
 * The Three.js implementation of {@link Renderer}, and the only place in the repository that imports
 * `three`.
 *
 * It holds one mesh per entity and reconciles that set against what the render system submits each
 * frame: unseen meshes are removed at {@link ThreeRenderer.endFrame}, which is why nothing outside ever
 * has to tell it that an entity went away.
 *
 * Sizing is not its business. The canvas' drawing buffer belongs to `CanvasSurface`, so the pixel ratio
 * stays at one here and the viewport and scissor rectangles are set explicitly from the
 * {@link Viewport} it is handed. Calling Three's `setSize` would fight the surface for the same canvas.
 */
export class ThreeRenderer implements Renderer {
  readonly #renderer: WebGLRenderer;
  readonly #scene = new ThreeScene();
  readonly #camera = new ThreeOrthographicCamera(-1, 1, 1, -1, CAMERA_NEAR, CAMERA_FAR);
  readonly #geometry = new PlaneGeometry(1, 1);
  readonly #sprites = new Map<EntityId, RetainedSprite>();
  readonly #clearColor = new ThreeColor(0, 0, 0);

  #clearAlpha = 1;
  #rect: WebGLRect | undefined;
  #disposed = false;

  constructor(options: ThreeRendererOptions) {
    this.#renderer = new WebGLRenderer({
      canvas: options.canvas,
      antialias: options.antialias ?? true,
      alpha: true,
    });
    // The surface owns the drawing buffer size, so Three must not scale anything behind its back.
    this.#renderer.setPixelRatio(1);
    this.#renderer.autoClear = false;
    this.#renderer.outputColorSpace = SRGBColorSpace;
    this.#camera.position.z = CAMERA_DISTANCE;
  }

  /** Adopts a new canvas size and viewport, including the letterbox bars around the drawn area. */
  resize(size: CanvasSize, viewport: Viewport): void {
    this.#rect = toWebGLRect(size, viewport);
  }

  /** Sets the color the drawn area is cleared to. The letterbox bars stay black. */
  setClearColor(color: Color): void {
    this.#clearColor.setRGB(color.r, color.g, color.b, SRGBColorSpace);
    this.#clearAlpha = color.a;
  }

  /** Opens a frame: adopts the camera and marks every retained sprite as not yet seen. */
  beginFrame(camera: Camera): void {
    this.#syncCamera(camera);
    for (const sprite of this.#sprites.values()) {
      sprite.seen = false;
    }
  }

  /** Creates or updates the mesh for an entity. */
  drawSprite(entityId: EntityId, sprite: SpriteDrawCall): void {
    const retained = this.#sprites.get(entityId) ?? this.#createSprite(entityId);
    retained.seen = true;
    retained.sortOrder = sprite.sortOrder;

    this.#applyAppearance(retained.mesh.material, sprite);
    this.#applyPlacement(retained.mesh, sprite);
  }

  /** Removes everything not drawn this frame, fixes the draw order and presents. */
  endFrame(): void {
    this.#removeUnseenSprites();
    this.#applyDrawOrder();
    this.#clearFrame();
    this.#renderer.render(this.#scene, this.#camera);
  }

  /** Uploads an image and returns the handle {@link SpriteDrawCall.texture} expects. */
  createTexture(source: TextureSource): Texture {
    const texture = new ThreeTexture(source);
    texture.colorSpace = SRGBColorSpace;
    texture.needsUpdate = true;
    return new ThreeTextureHandle(texture, source.width, source.height);
  }

  /** Releases every mesh, the shared geometry and the WebGL context. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;

    for (const sprite of this.#sprites.values()) {
      this.#destroySprite(sprite);
    }
    this.#sprites.clear();
    this.#geometry.dispose();
    this.#renderer.dispose();
  }

  #createSprite(entityId: EntityId): RetainedSprite {
    /*
     * Every sprite is transparent and depth-tested off, so all of them land in Three's transparent
     * pass and the explicit renderOrder set in #applyDrawOrder is the only thing deciding what covers
     * what. Leaving depth on would let the z buffer overrule sortOrder.
     */
    const material = new MeshBasicMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const mesh = new Mesh(this.#geometry, material);
    this.#scene.add(mesh);

    const retained: RetainedSprite = { entityId, mesh, sortOrder: 0, seen: true };
    this.#sprites.set(entityId, retained);
    return retained;
  }

  #applyAppearance(material: MeshBasicMaterial, sprite: SpriteDrawCall): void {
    material.color.setRGB(sprite.color.r, sprite.color.g, sprite.color.b, SRGBColorSpace);
    material.opacity = sprite.color.a;

    const map = this.#resolveTexture(sprite.texture);
    if (material.map !== map) {
      material.map = map;
      material.needsUpdate = true;
    }
  }

  #resolveTexture(texture: Texture | undefined): ThreeTexture | null {
    if (texture === undefined) {
      return null;
    }
    if (!(texture instanceof ThreeTextureHandle)) {
      throw new Error('This texture was not created by ThreeRenderer.createTexture.');
    }
    return texture.texture;
  }

  /*
   * The quad's own origin is its center, so an anchor anywhere else becomes an offset from the
   * anchor point to that center, rotated with the sprite and already scaled by its size.
   */
  #applyPlacement(mesh: Mesh<PlaneGeometry, MeshBasicMaterial>, sprite: SpriteDrawCall): void {
    const offsetX = (0.5 - sprite.anchorX) * sprite.width;
    const offsetY = (0.5 - sprite.anchorY) * sprite.height;
    const cos = Math.cos(sprite.rotation);
    const sin = Math.sin(sprite.rotation);

    mesh.position.set(
      sprite.x + rotatedX(offsetX, offsetY, cos, sin),
      sprite.y + rotatedY(offsetX, offsetY, cos, sin),
      SPRITE_DEPTH,
    );
    mesh.rotation.z = sprite.rotation;
    mesh.scale.set(sprite.width, sprite.height, 1);
  }

  #removeUnseenSprites(): void {
    for (const [entityId, sprite] of this.#sprites) {
      if (sprite.seen) {
        continue;
      }
      this.#destroySprite(sprite);
      this.#sprites.delete(entityId);
    }
  }

  #destroySprite(sprite: RetainedSprite): void {
    this.#scene.remove(sprite.mesh);
    sprite.mesh.material.dispose();
  }

  #applyDrawOrder(): void {
    const ordered = [...this.#sprites.values()].sort(compareSpriteDrawOrder);
    for (const [index, sprite] of ordered.entries()) {
      sprite.mesh.renderOrder = index;
    }
  }

  /*
   * Two clears, because the bars and the playfield are different surfaces: the whole buffer goes black
   * with the scissor off, then the drawn area is cleared again inside it.
   */
  #clearFrame(): void {
    const renderer = this.#renderer;
    renderer.setScissorTest(false);
    renderer.setClearColor(LETTERBOX_COLOR, 1);
    renderer.clear(true, true, false);

    const rect = this.#rect;
    if (rect !== undefined) {
      renderer.setViewport(rect.x, rect.y, rect.width, rect.height);
      renderer.setScissor(rect.x, rect.y, rect.width, rect.height);
      renderer.setScissorTest(true);
    }
    renderer.setClearColor(this.#clearColor, this.#clearAlpha);
    renderer.clear(true, true, false);
  }

  #syncCamera(camera: Camera): void {
    const halfWidth = camera.viewport.worldWidth / 2;
    const halfHeight = camera.viewport.worldHeight / 2;
    const three = this.#camera;

    if (three.right !== halfWidth || three.top !== halfHeight) {
      three.left = -halfWidth;
      three.right = halfWidth;
      three.top = halfHeight;
      three.bottom = -halfHeight;
      three.updateProjectionMatrix();
    }
    three.position.set(camera.position.x, camera.position.y, CAMERA_DISTANCE);
  }
}
